import { useQuery } from '@tanstack/react-query';

import { authService } from '@/services';
import { sessionsKeys } from '@/services/auth-client/sessions.keys';
import type { Session } from '@/services/auth-client/types';

interface UseSession {
  session: Session | null;
  /** `true` só no primeiro carregamento — depois disso a sessão vem do cache. */
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * Há sessão, mas ela é a conta anônima do socorro sem cadastro (DV-24).
   *
   * ⚠️ `isAuthenticated` é `true` junto com esta — e é assim mesmo: para a RLS a
   * sessão anônima **é** uma sessão. A diferença é de cadastro, não de
   * autenticação, e quem precisa dela (a guarda de rota, a tela de conclusão do
   * SOS, o perfil) pergunta por aqui em vez de inspecionar `email == null`.
   */
  isAnonymous: boolean;
}

/**
 * A sessão atual, disponível para o app inteiro.
 *
 * Mora em `compositions/` porque é infraestrutura consumida por qualquer área —
 * guarda de rota, tela de perfil, pop-up de "login necessário" —, e
 * `compositions/` é onde docs/01-arquitetura.md coloca hook cross-module. Ela
 * chega pelo composition root (`@/services`), então este arquivo não conhece o
 * módulo de auth nem o adapter que está no ar.
 *
 * ## Por que react-query e não Context
 *
 * A tabela de estado de docs/04 sugere Context para "valor estável de
 * infraestrutura", e a sessão é isso. Mas ela também é **buscada**: tem carregando,
 * tem erro, e precisa ser reavaliada depois de login, cadastro e logout. Com
 * Context, essas três invalidações viram um `setState` manual em cada mutação — e
 * a primeira que esquecer deixa o app achando que ainda há sessão. Com react-query
 * é `invalidateQueries(sessionsKeys.all)`, do mesmo jeito que toda outra entidade.
 *
 * `staleTime: Infinity` porque a sessão não muda sozinha: ela muda por ação do
 * usuário, e essas ações invalidam a chave explicitamente.
 */
export function useSession(): UseSession {
  const { data, isPending } = useQuery<Session | null>({
    queryKey: sessionsKeys.current(),
    queryFn: () => authService.getSession(),
    staleTime: Infinity,
    // Sessão ausente é resposta legítima, não falha de rede: repetir a consulta
    // só atrasaria a primeira pintura de quem não está logado.
    retry: false,
  });

  return {
    session: data ?? null,
    isLoading: isPending,
    isAuthenticated: Boolean(data),
    isAnonymous: data?.isAnonymous ?? false,
  };
}
